import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createDefaultCauseSet,
  deleteDefaultCauseSet,
  fetchDefaultCauseSets,
  updateDefaultCauseSet,
} from "@/api/system";

const key = ["system", "default-causes"];

/** Every rotation, newest first. */
export function useDefaultCauseSets() {
  return useQuery({ queryKey: key, queryFn: fetchDefaultCauseSets });
}

/**
 * Rotate the default list.
 *
 * Invalidates the list itself and nothing else: the change applies to credits minted from its date
 * onwards, so no wallet, lot or campaign figure on screen moves because of it.
 */
export function useCreateDefaultCauseSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDefaultCauseSet,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

/** Change which charities a list contains. Scheduled or in force only. */
export function useUpdateDefaultCauseSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, causeProductIds }: { id: number; causeProductIds: string[] }) =>
      updateDefaultCauseSet(id, causeProductIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

/** Remove a list that hasn't started yet — the fix for a mistyped date. */
export function useDeleteDefaultCauseSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDefaultCauseSet,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}
